import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ArrowLeft, ArrowRight } from '@mui/icons-material';
import { getTodayDate } from '../../utils/date'
import FullCallendar from '@fullcalendar/react'
import esLocale from '@fullcalendar/core/locales/es';
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { Loader } from '../../components/Loader';
import { NewEventForm } from './NewEventForm';
import { IEventoCalendario, IAmenidad } from '../../models/reservas';
import { Alert } from '../../components/Alert';
import './Disponibilidad.css';
import dayjs from 'dayjs';

interface IDisponibilidadProps {
  eventos: IEventoCalendario[];
  loadingEventos: boolean;
  setEventos: (eventos: IEventoCalendario[] | ((prev: IEventoCalendario[]) => IEventoCalendario[])) => void;
  nextStep?: () => void;
  nextText?: string;
  prevStep?: () => void;
  prevText?: string;
  reservaAction?: () => void;
  amenidad?: number | string;
  amenidadesList?: IAmenidad[];
}

export const Disponibilidad = ({
  nextStep, loadingEventos, setEventos, eventos,
  nextText, prevStep, prevText, reservaAction,
  amenidad, amenidadesList
}: IDisponibilidadProps) => {
  const calendar = useRef<any>();
  const [openNewEventForm, setOpenNewEventForm] = useState(false);
  const [dateStr, setDateStr] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertOkText, setAlertOkText] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [disabled, setDisabled] = useState(!isNaN(Number([...eventos][eventos.length - 1]?.id ?? -1)));
  const [horarioSeleccionado, setHorarioSeleccionado] = useState(false);
  const [eventoCreado, setEventoCreado] = useState(false);
  const [currentViewType, setCurrentViewType] = useState<string>('dayGridMonth');

  // Ajustar solo en vista diaria los eventos que cruzan medianoche
  const eventsForView = React.useMemo(() => {
    if (currentViewType !== 'timeGridDay') return eventos;
    return eventos.map(ev => {
      if (!ev?.end) return ev;
      const start = dayjs(ev.start);
      const end = dayjs(ev.end);
      if (end.valueOf() <= start.valueOf()) {
        return {
          ...ev,
          end: end.add(1, 'day').format('YYYY-MM-DDTHH:mm:00')
        };
      }
      return ev;
    });
  }, [eventos, currentViewType]);

  // Verifica si una hora (slot) está ocupada por un evento existente
  const isSlotOccupied = (slotDate?: Date) => {
    if (!slotDate) return false;
    if (currentViewType !== 'timeGridDay') return false;
    const slot = dayjs(slotDate);
    return eventsForView.some(ev => {
      if (!ev?.start || !ev?.end) return false;
      const start = dayjs(ev.start);
      const end = dayjs(ev.end);
      return slot.valueOf() >= start.valueOf() && slot.valueOf() < end.valueOf();
    });
  };

  useEffect(() => {
    if (eventos.length === 0 || !eventos[eventos.length - 1]?.start) return;

    const lastEvent = eventos[eventos.length - 1];
    const eventDate = new Date(lastEvent.start);
    const now = new Date();

    const isSameDay =
      eventDate.getFullYear() === now.getFullYear() &&
      eventDate.getMonth() === now.getMonth() &&
      eventDate.getDate() === now.getDate();

    if (!isSameDay) return;

    const diffMinutes = (eventDate.getTime() - now.getTime()) / (1000 * 60);

    if (diffMinutes > 0 && diffMinutes < 30) {
      setDisabled(true);
      appAlert("La reservación se debe realizar con al menos 1 hora de anticipación.");

      setEventos(prev => prev.slice(0, -1));
      setHorarioSeleccionado(false);
      setEventoCreado(false);
    }
  }, [eventos]);

  useEffect(() => {
    setDisabled(!isNaN(Number([...eventos][eventos.length - 1]?.id ?? -1)));
  }, [eventos]);

  useEffect(() => {
    setHorarioSeleccionado(false);
    setEventoCreado(false);
  }, [amenidad]);

  useEffect(() => {
    if (eventos.length > 0) {
      const lastEvent = eventos[eventos.length - 1];
      if (lastEvent && isNaN(Number(lastEvent.id))) {
        setEventoCreado(true);
        setHorarioSeleccionado(true);
      }
    }
  }, [eventos]);

  const appAlert = (message: string, okText: string = '') => {
    setShowAlert(true);
    setAlertOkText(okText);
    setAlertMessage(message);
  }

  const handlePrev = () => {
    const calendarAPI = calendar.current.getApi();
    calendarAPI.prev();
  };

  const handleNext = () => {
    const calendarAPI = calendar.current.getApi();
    calendarAPI.next();
  };

  const changeCallendarView = (view: string, date: string) => {
    const calendarAPI = calendar.current.getApi();
    calendarAPI.changeView(view, date);
  };

  const handleDateClick = (arg: any) => {
    if (horarioSeleccionado || eventoCreado) {
      if (eventos.length > 0 && isNaN(Number(eventos[eventos.length - 1]?.id))) {
        setEventos(prev => prev.slice(0, -1));
      }
      setHorarioSeleccionado(false);
      setEventoCreado(false);
    }

    const today = getTodayDate();
    const selectedDate = arg?.dateStr;
    const todayDate = today.split('T')[0];
    if (selectedDate < todayDate) return appAlert("Solo seleccione días posteriores o el día de hoy.");

    const amenidadInfo = amenidadesList?.find(ameni => ameni.servicio === Number(amenidad));
    if (amenidadInfo && amenidadInfo.horas_Anticipadas && amenidadInfo.horas_Anticipadas > 0) {
      const now = new Date();
      const selectedStartOfDay = new Date(arg.date);
      selectedStartOfDay.setHours(0, 0, 0, 0);
      const diffInMs = selectedStartOfDay.getTime() - now.getTime();
      const diffInHours = diffInMs / (1000 * 60 * 60);

      if (diffInHours > amenidadInfo.horas_Anticipadas) {
        return appAlert(`Esta amenidad solo se puede reservar con ${amenidadInfo.horas_Anticipadas} horas de anticipación.`);
      }
    }

    const day = arg.date.getDay();
    const dias = [
      amenidadInfo?.domingo,
      amenidadInfo?.lunes,
      amenidadInfo?.martes,
      amenidadInfo?.miercoles,
      amenidadInfo?.jueves,
      amenidadInfo?.viernes,
      amenidadInfo?.sabado,
    ]
    if (!dias[day]) return appAlert('Día no disponible para reservar.');

    if (arg?.view?.type !== 'timeGridDay')
      changeCallendarView("timeGridDay", arg?.dateStr);
    else {
      const now = new Date();
      if (arg.date < now) {
        return appAlert("No puede seleccionar una hora anterior a la hora actual.");
      }
      // Bloquear selección en horas ocupadas
      if (isSlotOccupied(arg.date)) {
        return appAlert("La hora seleccionada ya está reservada. Elija otra franja horaria.");
      }
      const amenidadInfo = amenidadesList?.find(ameni => ameni.servicio === Number(amenidad));
      if (amenidadInfo && amenidadInfo.horas_Anticipadas && amenidadInfo.horas_Anticipadas > 0) {
        const diffInMs = arg.date.getTime() - now.getTime();
        const diffInHours = diffInMs / (1000 * 60 * 60);
        if (diffInHours > amenidadInfo.horas_Anticipadas) {
          return appAlert(`Esta amenidad solo se puede reservar con ${amenidadInfo.horas_Anticipadas} horas de anticipación.`);
        }
      }
      
      // Validación para minutos de preparación
      if (amenidadInfo && amenidadInfo.minutos_Preparacion && amenidadInfo.minutos_Preparacion > 0) {
        const diffInMs = arg.date.getTime() - now.getTime();
        const diffInMinutes = diffInMs / (1000 * 60);
        if (diffInMinutes < amenidadInfo.minutos_Preparacion) {
          return appAlert(`Esta amenidad requiere al menos ${amenidadInfo.minutos_Preparacion} minutos de preparación.`);
        }
      }
      
      const selectedHour = arg.date.getHours();
      const selectedMinute = arg.date.getMinutes();

      if (amenidadInfo) {
        const horaInicio = amenidadInfo.inicio_Amenidad;
        const horaFin = amenidadInfo.fin_Amenidad;

        const selectedTimeInMinutes = selectedHour * 60 + selectedMinute;
        const startTimeInMinutes = horaInicio * 60;
        const endTimeInMinutes = horaFin * 60;

        if (selectedTimeInMinutes < startTimeInMinutes || selectedTimeInMinutes >= endTimeInMinutes) {
          return appAlert(`Esta amenidad solo está disponible de ${horaInicio}:00 a ${horaFin}:00 hrs.`);
        }
      }

      setHorarioSeleccionado(true);
      setOpenNewEventForm(true);
      // Normalizar a hora local para evitar desfases por zona horaria
      setDateStr(dayjs(arg.date).format('YYYY-MM-DDTHH:mm:00'));
    }
  }
  return (
    <>

      <NewEventForm
        activeForm={openNewEventForm}
        setActiveForm={(active: boolean) => {
          setOpenNewEventForm(active);
          if (!active && !eventoCreado) {
            setHorarioSeleccionado(false);
          }
        }}
        setEvents={setEventos}
        dateStr={dateStr}
        infoAmenidad={amenidadesList?.find(ameni => ameni.servicio === Number(amenidad))}
        alertAction={(msg: string) => appAlert(msg)}
      />

      <Alert
        message={alertMessage}
        okText={alertOkText}
        show={showAlert}
        setShow={(show: boolean) => setShowAlert(show)}
        type='warning'
      />

      <Box>
        Verifique la disponibilidad:
      </Box>
      <Box>
        <Typography variant="body2" component="div" gutterBottom marginY='1em' textAlign={'left'}>
          {(() => {
            const amenidadInfo = amenidadesList?.find(ameni => ameni.servicio === Number(amenidad));
            return (
              <>
                Amenidad: {amenidadInfo?.nombre}
                <br />
                Horario: {amenidadInfo?.inicio_Amenidad} hrs - {amenidadInfo?.fin_Amenidad} hrs
                <br />
                Tiempo máximo: {amenidadInfo?.tiempo_Maximo} hrs
                {amenidadInfo?.minutos_Preparacion && amenidadInfo.minutos_Preparacion > 0 && (
                  <>
                    <br />
                    Minutos de preparación: {amenidadInfo.minutos_Preparacion} minutos
                  </>
                )}
                {amenidadInfo?.horas_Anticipadas && amenidadInfo.horas_Anticipadas > 0 && (
                  <>
                    <br />
                    Reserva previa: {amenidadInfo.horas_Anticipadas} horas
                  </>
                )}
              </>
            );
          })()}
        </Typography>
      </Box>
      <Box
        display={'flex'}
        alignItems={'stretch'}
        paddingBottom={'10rem'}
        justifyContent={'center'}
        flexDirection={'column'}
        width={'90%'}
      >
        <Box
          marginY={'1rem'}
          display={'flex'}
          justifyContent={'space-between'}
        >
          <Box>
            <Button color='primary' variant='contained' onClick={handlePrev}><ArrowLeft /></Button>
            <Button color='primary' variant='contained' onClick={handleNext}><ArrowRight /></Button>
          </Box>
          <Button color='primary' variant='contained' onClick={() => {
            const calendarAPI = calendar.current.getApi();
            console.log(eventos)
            calendarAPI.today();
          }}>Hoy</Button>
          {(horarioSeleccionado || eventoCreado) && (
            <Button
              color='secondary'
              variant='outlined'
              onClick={() => {
                setHorarioSeleccionado(false);
                setEventoCreado(false);
                if (eventos.length > 0 && isNaN(Number(eventos[eventos.length - 1]?.id))) {
                  setEventos(prev => prev.slice(0, -1));
                }
              }}
              sx={{ marginLeft: '0.5rem' }}
            >
              Cancelar Selección
            </Button>
          )}
        </Box>
        {
          loadingEventos
            ? <Loader
              loadingText={'Cargando eventos...'}
            />
            : <FullCallendar
              locale={esLocale}
              ref={calendar}
              expandRows={true}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              dateClick={handleDateClick}
              headerToolbar={{
                left: 'title',
                center: 'dayGridMonth,timeGridDay',
                right: '',
              }}
              datesSet={(arg) => setCurrentViewType(arg.view.type)}
              height={500}
              events={[...eventsForView]}
              dayCellContent={(arg) => {
                const day = arg.date.getDay();
                const amenidadInfo = amenidadesList?.find(ameni => ameni.servicio === Number(amenidad));
                const dias = [
                  amenidadInfo?.domingo,
                  amenidadInfo?.lunes,
                  amenidadInfo?.martes,
                  amenidadInfo?.miercoles,
                  amenidadInfo?.jueves,
                  amenidadInfo?.viernes,
                  amenidadInfo?.sabado,
                ]
                const dayNumberText = arg.dayNumberText.replace('de', '').replace('.', '');

                if (!dias[day]) {
                  return (
                    <div className="fc-daygrid-day-frame fc-daygrid-day-frame--disabled">
                      <div className='fc-daygrid-day-number'>{dayNumberText}</div>
                      <div className='sin-atencion-text'>SIN ATENCIÓN</div>
                    </div>
                  );
                }

                return dayNumberText;
              }}
              views={{
                dayGrid: {
                  fixedWeekCount: false,
                  showNonCurrentDates: false,
                  titleFormat: {
                    year: '2-digit',
                    month: 'long'
                  }
                },
                timeGridDay: {
                  slotMinTime: amenidadesList?.find(ameni => ameni.servicio === Number(amenidad))?.inicio_Amenidad + ':00:00',
                  slotMaxTime: amenidadesList?.find(ameni => ameni.servicio === Number(amenidad))?.fin_Amenidad + ':00:00',
                  slotDuration: '00:15:00',
                  slotLabelFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }
                }
              }}
              slotLaneClassNames={(arg) => {
                if (currentViewType === 'timeGridDay' && isSlotOccupied(arg.date)) {
                  return ['disabled-slot'];
                }
                return ['available-slot'];
              }}
            />
        }
      </Box>

      <Box
        display={'flex'}
        justifyContent={prevStep && nextStep ? 'space-between' : 'center'}
        position={'fixed'}
        bottom={0}
        left={0}
        width={'calc(100% - 2rem)'}
        borderTop={'1px solid #AAA'}
        paddingY={'1rem'}
        bgcolor={'white'}
        zIndex={30}
        paddingX={'1rem'}
      >
        {prevStep &&
          <Button
            color='primary'
            variant="outlined"
            sx={{ width: '40%' }}
            onClick={prevStep}
          >
            {prevText}
          </Button>}
        {nextStep &&
          <Button
            color='primary'
            variant="outlined"
            sx={{ width: '40%' }}
            onClick={nextStep}
            disabled={disabled}
          >
            {nextText}
          </Button>}
        {reservaAction &&
          <Button color='primary' variant="contained" sx={{ width: '40%', marginX: '0.5rem' }} onClick={reservaAction}>
            Reservar
          </Button>}
      </Box>
    </>
  )
}